import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useTranslation from '../../hooks/useTranslation';
import Button from '../common/Button';
import styles from './Header.module.css';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { LEARN_ROUTES } from '../../config/learnRoutes';
import NotificationBell from './NotificationBell';

// The account menu, shared by the desktop dropdown and the narrow-screen menu. Under 768px
// the header hides .authSection (dropdown included), so the same links are repeated inside
// the hamburger menu; one list keeps the two from drifting apart.
const ACCOUNT_LINKS = [
  { to: '/me', label: (t) => t('nav.profile') },
  { to: '/me/invites', label: (t) => t('nav.myInvites') },
  { to: '/me/reputation', label: (t) => t('nav.reputation') },
  { to: '/security', label: () => '🔒 Security' },
];

// KAN-552 follow-up: a test runner who is not an admin cannot reach the admin dashboard, so the
// internal test-run page gets its own account-menu entry. Admins reach it from the dashboard.
// Placeholder label pending Paul's copy.
const TEST_RUNS_LINK = { to: '/admin/test-runs', label: () => 'Test runs', testId: 'account-test-runs' };

const Header = () => {
  const { user, isAuthenticated, isAdmin, isEditor, canEnterTestRuns, logout } = useAuth();
  const accountLinks = canEnterTestRuns && !isAdmin ? [...ACCOUNT_LINKS, TEST_RUNS_LINK] : ACCOUNT_LINKS;
  const { t, lang, setLang } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isActive = (path) => location.pathname.startsWith(path);
  const initials = user?.name ? user.name.charAt(0).toUpperCase() : '?';

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserDropdownOpen(false);
  }, [location.pathname]);

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  const handleLogout = async () => {
    setUserDropdownOpen(false);
    setMobileMenuOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <Link to="/">
            <img src="/static/novara-mark.png" alt="" className={styles.logoMark} />
            <span className={styles.logoText}>Novara</span>
          </Link>
        </div>

        <nav className={`${styles.nav} ${mobileMenuOpen ? styles.navOpen : ''}`} aria-label="Main navigation">
          {/* New Word Prize — hidden from MVP (KAN-130, FEATURE_FLAGS.NEW_WORDS) */}
          {FEATURE_FLAGS.NEW_WORDS && (
            <Link to="/new-word-prize" className={`${styles.navLink} ${isActive('/new-word-prize') ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon} aria-hidden="true">💬</span>
              {t('nav.newWordPrize')}
            </Link>
          )}
          {/* KAN-273 reworked flow: the dashboard is the hub. The FC assessment, the
              map and the profile build are reached from its to-do list, not from here —
              so the nav offers one door, and every surface's own exit is "return to
              dashboard". With CCS off, the FC assessment link stands in. */}
          {FEATURE_FLAGS.CCS ? (
            <Link to={LEARN_ROUTES.dashboard} className={`${styles.navLink} ${isActive('/learn') ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon} aria-hidden="true">✦</span>
              Dashboard
            </Link>
          ) : (
            <Link to={LEARN_ROUTES.fc} className={`${styles.navLink} ${isActive(LEARN_ROUTES.fc) ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon} aria-hidden="true">📊</span>
              {t('nav.test')}
            </Link>
          )}
          {/* Admin panel — admins only; members never see the link. Content desk lives inside.
              Editors (not admin) get a direct Content desk link instead. */}
          {isAdmin && (
            <Link to="/admin" className={`${styles.navLink} ${isActive('/admin') ? styles.navLinkActive : ''}`} data-testid="nav-admin">
              <span className={styles.navIcon} aria-hidden="true">⚙︎</span>
              Admin
            </Link>
          )}
          {isEditor && !isAdmin && (
            <Link to="/admin/content" className={`${styles.navLink} ${isActive('/admin/content') ? styles.navLinkActive : ''}`} data-testid="nav-content-desk">
              <span className={styles.navIcon} aria-hidden="true">🗂️</span>
              Content desk
            </Link>
          )}
          {/* Capability agent. A route nobody can reach is the same defect as no route, and
              this endpoint spent months reachable-in-principle with zero callers — so the
              link ships WITH the page, not after it. Ungated for the same reason the route
              is: the memory loop does not depend on the bout runner. */}
          <Link to="/learn/agent" className={`${styles.navLink} ${isActive('/learn/agent') ? styles.navLinkActive : ''}`}>
            <span className={styles.navIcon} aria-hidden="true">◍</span>
            Agent
          </Link>
          {/* Thought Challenge — hidden from MVP (KAN-130, FEATURE_FLAGS.CHALLENGE_SUBMISSION) */}
          {FEATURE_FLAGS.CHALLENGE_SUBMISSION && (
            <Link to="/thought-challenge" className={`${styles.navLink} ${isActive('/thought-challenge') || isActive('/challenges') ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon} aria-hidden="true">🧠</span>
              {t('nav.thoughtChallenge')}
            </Link>
          )}
          {/* Learning Theory — hidden from MVP (KAN-209, FEATURE_FLAGS.LEARNING_THEORY) */}
          {FEATURE_FLAGS.LEARNING_THEORY && (
            <Link to="/learning-theory" className={`${styles.navLink} ${isActive('/learning-theories') || isActive('/learning-theory') ? styles.navLinkActive : ''}`}>
              <span className={styles.navIcon} aria-hidden="true">📚</span>
              {t('nav.learningTheory')}
            </Link>
          )}
          {/* Account area for narrow screens. Hidden on desktop (the dropdown in .authSection
              serves it there); shown only inside the open mobile menu. */}
          <div className={styles.mobileAccount} data-testid="mobile-account">
            {isAuthenticated ? (
              <>
                <span className={styles.mobileAccountName}>{user?.name}</span>
                {accountLinks.map(({ to, label, testId }) => (
                  <Link
                    key={to}
                    to={to}
                    data-testid={testId ? `mobile-${testId}` : undefined}
                    className={`${styles.navLink} ${location.pathname === to ? styles.navLinkActive : ''}`}
                  >
                    {label(t)}
                  </Link>
                ))}
                <button type="button" className={`${styles.navLink} ${styles.mobileLogout}`} onClick={handleLogout}>
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={styles.navLink}>{t('nav.login')}</Link>
                <Link to="/register" className={styles.navLink}>{t('nav.register')}</Link>
              </>
            )}
          </div>
        </nav>

        {/* Unread-notification badge (KAN-533); renders nothing when signed out. Kept outside
            authSection, which is hidden under 768px, so the bell shows on narrow screens too. */}
        <div className={styles.bellSlot} data-testid="header-bell-slot">
          <NotificationBell />
        </div>

        <div className={styles.authSection}>
          {/* Language toggle — controlled by FEATURE_FLAGS.LANGUAGE_SWITCHER in src/config/featureFlags.js */}
          {FEATURE_FLAGS.LANGUAGE_SWITCHER && (
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '0.75rem', gap: '2px', background: '#f3f4f6', borderRadius: '6px', padding: '2px' }}>
            <button
              onClick={() => setLang('en')}
              style={{
                padding: '3px 8px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: lang === 'en' ? '700' : '400',
                background: lang === 'en' ? '#fff' : 'transparent',
                color: lang === 'en' ? '#111' : '#6b7280',
                boxShadow: lang === 'en' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
              aria-label="Switch to English"
            >
              EN
            </button>
            <button
              onClick={() => setLang('zh')}
              style={{
                padding: '3px 8px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: lang === 'zh' ? '700' : '400',
                background: lang === 'zh' ? '#fff' : 'transparent',
                color: lang === 'zh' ? '#111' : '#6b7280',
                boxShadow: lang === 'zh' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
              aria-label="切换到中文"
            >
              中文
            </button>
          </div>
          )}

          {isAuthenticated ? (
            <div className={styles.userMenu} ref={dropdownRef}>
              <button
                className={styles.userButton}
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                aria-expanded={userDropdownOpen}
                aria-haspopup="true"
                aria-label={`User menu for ${user?.name || 'user'}`}
              >
                <span className={styles.userAvatar} aria-hidden="true">{initials}</span>
                <span className={styles.userName}>{user?.name}</span>
                <span className={styles.dropdownIcon} aria-hidden="true">▼</span>
              </button>

              {userDropdownOpen && (
                <div className={styles.dropdown} role="menu">
                  {accountLinks.map(({ to, label, testId }) => (
                    <Link
                      key={to}
                      to={to}
                      data-testid={testId}
                      className={styles.dropdownItem}
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      {label(t)}
                    </Link>
                  ))}
                  <hr className={styles.divider} />
                  <button
                    className={styles.dropdownItem}
                    onClick={handleLogout}
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.authButtons}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/login')}
              >
                {t('nav.login')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/register')}
              >
                {t('nav.register')}
              </Button>
            </div>
          )}
        </div>

        <button
          className={styles.hamburger}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
      </div>
    </header>
  );
};

export default Header;
